"use client";

import { z } from "zod";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/common/components-2/accordion";
import { Button } from "@/common/components-2/button";
import { MigrationLink } from "@/common/components-2/migration-link";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { Section } from "@/common/components-2/section";
import { CmsSectionHeading } from "@/common/components-2/section-heading/cms-section-heading";
import { captureException } from "@/common/helpers/capture-exception";
import type { HranoPage } from "@/payload-types";
import { EmitErrorReport } from "@/services/cms/components/emit-error-report";

const faqSectionSchema = z.object({
  title: z.string(),
  heading: z.string(),
  items: z.array(z.object({ title: z.string(), description: z.string() })),
  button: z.object({ label: z.string(), href: z.string() }),
});

export function FaqSection(props: HranoPage["fs"]) {
  let propsData: z.infer<typeof faqSectionSchema>;

  try {
    propsData = faqSectionSchema.parse(props);
  } catch (ex) {
    captureException(ex);
    return <EmitErrorReport error={JSON.stringify(ex)} name="FAQ section" />;
  }

  const { title, heading, items, button } = propsData;

  return (
    <Section classes={{ container: "items-center py-12 lg:py-20 xl:py-24" }} id="faq">
      <CmsSectionHeading heading={heading} title={title} classes={{ container: "md:m-0 md:self-start" }} />
      <Accordion type="single" collapsible className="mt-8 max-w-(--breakpoint-sm)">
        {items.map((item) => (
          <AccordionItem key={item.title} value={item.title}>
            <AccordionTrigger className="font-sans text-2xl lg:text-2xl">{item.title}</AccordionTrigger>
            <AccordionContent>
              <SanitizedContent as="div" content={item.description} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <Button className="mt-8 max-md:w-full md:mt-16" variant="secondary" asChild size="lg">
        <MigrationLink href={button.href}>{button.label}</MigrationLink>
      </Button>
    </Section>
  );
}
